// src/services/follow-api.ts

import { FOLLOW_EDIT_API, FOLLOW_SEARCH_API, FOLLOW_SELECT_API, FOLLOW_NOTICE_API, FOLLOW_GET_NOTICE_DESTINATION_API, ATTACHMENT_LIST_API, ATTACHMENT_DELETE_API, ATTACHMENT_DOWNLOAD_API, HOME_TODO_COUNT_API, FOLLOW_NOTICE_HISTORY_API, FOLLOW_UNREAD_API, LINKEDFOLLOW_ALL_API, LINKEDFOLLOW_SEARCH_API, LINKEDFOLLOW_ADD_LINK_API, LINKEDFOLLOW_CUT_API, LINKEDFOLLOW_CUT_ALL_API, FOLLOW_GET_COMMENTS_API, FOLLOW_SAVE_COMMENT_API, FOLLOW_DELETE_COMMENT_API } from '../config';
import { FollowRegistrationRequest, FollowRegistrationResponse, NotifRecipientWithDetails, FollowComment } from '../types/follow';
import { indexedDBStorage } from './storage/indexedDB';
import { logApiError } from '../utils/errorLogger';
import { fetchWithTokenRefresh } from './token-manager';
import { isoUtcToJstDate } from '../utils/dateUtils';

/** API レスポンスの followdate を JST 日付文字列 (YYYY-MM-DD) に正規化する */
const normalizeFollowdate = <T extends { followdate?: string }>(item: T): T => ({
  ...item,
  followdate: isoUtcToJstDate(item.followdate || ''),
});

/**
 * フォローコメントの生データ（followcommentlist の各要素、または selectFollow の followcomment 埋め込み配列の各要素）
 * を FollowComment（表示・CRUD共通の型）に変換する
 */
export const toFollowComment = (c: any): FollowComment => ({
  followcommentno: c.followcommentno,
  followno: c.followno,
  authorUserno: c.createuserno,
  author: c.createusername || c.createuser || '',
  text: c.commentcontent || '',
  createDate: typeof c.createdate === 'string' ? isoUtcToJstDate(c.createdate) : '',
  updateDate: typeof c.updatedate === 'string' ? isoUtcToJstDate(c.updatedate) : undefined,
});

/** 全APIリクエスト共通ヘッダー */
const JSON_HEADERS = {
  'Content-Type': 'application/json',
  'Cache-Control': 'no-cache, no-store',
  'Pragma': 'no-cache',
} as const;

/** getNoticeDestination レスポンス内の通知先ユーザー1件 */
export type NoticeDestinationUser = {
  userno: number;
  username: string;
  deptno: number;
  deptname: string;
  urgentflag: boolean;
  mailaddress: string;
};

/**
 * 通知先リストを組み立てる（現在はユーザー通知のみ有効）
 * TODO: Teams通知・メールアドレス通知を実装する際は以下のコメントアウトを解除:
 *   - メール・Teams のマッピング行（関数本体内）
 */
export const buildNoticeDestinationList = (
  recipients: { userno: number; urgent: boolean }[],
  //emailAddresses: string[],    // TODO: メールアドレス通知実装時は コメントアウトを解除
  //teamsChannelUrls: string[],  // TODO: Teams通知実装時は コメントアウトを解除
) => [
  ...recipients.map(r => ({ userno: r.userno, urgentflag: r.urgent })),
  // TODO: メールアドレス通知を実装する際はコメントアウトを解除
  // ...emailAddresses.map(mail => ({ mailaddress: mail, urgentflag: false as const })),
  // TODO: Teams通知を実装する際はコメントアウトを解除
  // ...teamsChannelUrls.map(url => ({ teamschannelurl: url })),
];

/**
 * 通知先モーダルの初期受信者リストを構築する
 * - 顧客に紐づく通知先ユーザー（getNoticeDestination）を取得
 * - フォロー担当者がログインユーザー以外で未含有なら末尾に追加
 *
 * @param customerno 顧客No（無指定/0 の場合は通知先取得をスキップし担当者のみで構築）
 * @param assigneeUserno フォロー担当者の userno
 * @param loginUserno ログインユーザーの userno（自身は通知対象外）
 * @param contactUsers 担当者の username/deptname 補完元
 * @param plantodoflag 予定/ToDoフラグ（getNoticeDestination に伝搬）
 */
export const buildInitialNotifRecipients = async (
  customerno: number | null | undefined,
  assigneeUserno: number | null,
  loginUserno: number | undefined,
  contactUsers: { userno: number; username: string; deptname: string }[],
  plantodoflag?: boolean,
): Promise<NotifRecipientWithDetails[]> => {
  const recipients: NotifRecipientWithDetails[] = [];

  if (customerno) {
    const noticeUsers = await followAPI.getNoticeDestination(customerno, plantodoflag);
    recipients.push(...noticeUsers.map(u => ({
      userno: u.userno,
      urgent: u.urgentflag ?? false,
      username: u.username,
      deptname: u.deptname,
    })));
  }

  if (assigneeUserno && assigneeUserno !== loginUserno && !recipients.some(r => r.userno === assigneeUserno)) {
    const assignee = contactUsers.find(u => u.userno === assigneeUserno);
    if (assignee) {
      recipients.push({
        userno: assignee.userno,
        urgent: false,
        username: assignee.username,
        deptname: assignee.deptname,
      });
    }
  }

  return recipients;
};

/**
 * フォロー情報に関するAPIサービス
 */
export const followAPI = {
  /**
   * フォロー情報を登録する
   * @param followData フォロー情報
   * @returns APIレスポンス
   */
  registerFollow: async (followData: FollowRegistrationRequest): Promise<FollowRegistrationResponse> => {
    try {
      // APIリクエスト送信（トークン管理は自動）
      const response = await fetchWithTokenRefresh(FOLLOW_EDIT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(followData),
      }, /* skipRetry: 二重登録防止のため自動再試行しない */ true);
      
      // レスポンスのステータスコードを確認
      if (!response.ok) {
        // エラーレスポンスの詳細を取得して記録
        let errorDetail = '';
        try {
          const errorResponse = await response.clone().text();
          errorDetail = errorResponse;
        } catch (readError) {
        }
        
        return { 
          success: false, 
          message: `APIエラー: ${response.status} ${response.statusText}`,
          error: errorDetail
        };
      }

      // レスポンスボディをJSONとして解析
      const data = await response.json();

      
      // 念のためレスポンスの内容をコンソールに出力（開発時のみ）
      if (process.env.NODE_ENV === 'development') {
      }
      
      // APIレスポンスを適切なフォーマットに変換
      // statuscode=200の場合、フォローNoを取得
      if (data.statuscode === 200 && data.followdata && data.followdata.followno) {
        return {
          success: true,
          data: {
            followno: data.followdata.followno.toString(),
            message: data.message || 'Success',
            followdata: data.followdata
          }
        };
      }
      
      // APIレスポンスが正常で、statuscode=200の場合
      if (data.statuscode === 200) {
        // フォローNoがない場合でも、登録自体は成功しているので
        // ダミーのフォローNoを設定して返す（実装目的のみ）
        // 実際の実装では、正しいAPIレスポンスが必要
        return {
          success: true,
          data: {
            followno: "999",  // ダミーのフォローNo
            message: data.message || "フォロー情報が正常に登録されました"
          },
          statuscode: 200
        };
      }
      
      // そのまま返す
      return data;
    } catch (error) {
      logApiError('followAPI.registerFollow', error, FOLLOW_EDIT_API, 'POST');
      return { 
        success: false, 
        message: 'サーバーとの通信に失敗しました' 
      };
    }
  },

  /**
   * フォロー一覧を検索する
   * @param params 顧客番号および仮登録除外フラグ
   * @returns フォロー一覧配列
   */
  searchFollowList: async (params: { customerno: number; excludeTemporary: boolean }): Promise<any[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SEARCH_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォロー一覧の取得に失敗しました');
      }
      return data.followlist || [];
    } catch (error) {
      logApiError('followAPI.searchFollowList', error, FOLLOW_SEARCH_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローを取得する
   * @param followno 取得するフォローNo
   */
  selectFollow: async (followno: number): Promise<any> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SELECT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followno }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォロー情報の取得に失敗しました');
      }

      return normalizeFollowdate(data.followdata);
    } catch (error) {
      logApiError('followAPI.selectFollow', error, FOLLOW_SELECT_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローを削除する
   * @param followno 削除するフォローNo
   */
  deleteFollow: async (followno: number): Promise<any> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_EDIT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followno, delflag: true }),
      }, /* skipRetry: 二重削除防止のため自動再試行しない */ true);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローの削除に失敗しました');
      }

      return data;
    } catch (error) {
      logApiError('followAPI.deleteFollow', error, FOLLOW_EDIT_API, 'POST');
      throw error;
    }
  },

  /**
   * 関連フォロー一覧を取得する
   * @param followno 対象フォローNo
   * @returns { follows: フォロー配列（followdateはJST正規化）, relationfollowgroupno: 群No（未所属時はnull） }
   */
  getRelatedFollows: async (followno: number): Promise<{ follows: any[]; relationfollowgroupno: number | null }> => {
    try {
      const response = await fetchWithTokenRefresh(LINKEDFOLLOW_ALL_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followNo: followno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '関連フォローの取得に失敗しました');
      }
      return {
        follows: (data.followlist || []).map(normalizeFollowdate),
        relationfollowgroupno: data.relationfollowgroupno ?? null,
      };
    } catch (error) {
      logApiError('followAPI.getRelatedFollows', error, LINKEDFOLLOW_ALL_API, 'POST');
      throw error;
    }
  },

  /**
   * 紐づけ対象として選択可能なフォローを検索する（自群を除外した全文検索）
   * @param params followNo（紐づけ元）＋検索条件・ページング
   * @returns { follows: フォロー配列, count: 総件数 }
   */
  searchLinkableFollows: async (params: {
    followNo: number;
    relationFollowGroupNo?: number | null;
    searchCompanyName?: string;
    searchCustomerName?: string;
    searchContactUserNoCsv?: string;
    viewCount?: number;
    listPage?: number;
    sortKey1?: string;
    sortAsc?: boolean;
  }): Promise<{ follows: any[]; count: number }> => {
    try {
      const response = await fetchWithTokenRefresh(LINKEDFOLLOW_SEARCH_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '紐づけ対象フォローの検索に失敗しました');
      }
      return {
        follows: (data.followlist || []).map(normalizeFollowdate),
        count: data.followlistsize ?? 0,
      };
    } catch (error) {
      logApiError('followAPI.searchLinkableFollows', error, LINKEDFOLLOW_SEARCH_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローの紐づけを追加する（群作成/スタンプ/マージは既存ロジックに委譲）
   * @param followNo 紐づけ元フォローNo
   * @param targetFollowNos 群未所属の紐づけ対象フォローNo配列（そのままスタンプ）
   * @param targetGroupNos 群所属の紐づけ対象が属する群No配列（群ごとマージ）
   * @param relationFollowGroupNo 紐づけ元が既に属する群No（あれば）
   * @returns 結果の関連フォロー群No
   */
  addFollowLink: async (
    followNo: number,
    targetFollowNos: number[],
    targetGroupNos: number[],
    relationFollowGroupNo?: number | null,
  ): Promise<number | null> => {
    try {
      const response = await fetchWithTokenRefresh(LINKEDFOLLOW_ADD_LINK_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({
          followNo,
          relationFollowGroupNo: relationFollowGroupNo ?? null,
          followNoCsv: targetFollowNos.join(','),
          relationFollowGroupNoCsv: targetGroupNos.join(','),
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローの紐づけに失敗しました');
      }
      return data.relationfollowgroupno ?? null;
    } catch (error) {
      logApiError('followAPI.addFollowLink', error, LINKEDFOLLOW_ADD_LINK_API, 'POST');
      throw error;
    }
  },

  /**
   * 指定フォローを関連フォロー群から切り離す（1件）
   * @param followNo 切り離し対象フォローNo
   * @param relationfollowgroupno 対象が属する群No
   * @returns 切り離し後の群No（群が解散した場合は null）
   */
  cutFollow: async (followNo: number, relationfollowgroupno: number | null): Promise<number | null> => {
    try {
      const response = await fetchWithTokenRefresh(LINKEDFOLLOW_CUT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followNo, relationfollowgroupno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローの切り離しに失敗しました');
      }
      return data.relationfollowgroupno ?? null;
    } catch (error) {
      logApiError('followAPI.cutFollow', error, LINKEDFOLLOW_CUT_API, 'POST');
      throw error;
    }
  },

  /**
   * 関連フォロー群を全解散する（全件切り離し）
   * @param followNo 起点フォローNo
   * @param relationfollowgroupno 対象群No
   */
  cutFollowAll: async (followNo: number, relationfollowgroupno: number | null): Promise<void> => {
    try {
      const response = await fetchWithTokenRefresh(LINKEDFOLLOW_CUT_ALL_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followNo, relationfollowgroupno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '関連フォロー群の解散に失敗しました');
      }
    } catch (error) {
      logApiError('followAPI.cutFollowAll', error, LINKEDFOLLOW_CUT_ALL_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローコメント一覧を取得する
   * @param followno 対象フォローNo
   */
  getComments: async (followno: number): Promise<FollowComment[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_GET_COMMENTS_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローコメントの取得に失敗しました');
      }
      return (data.followcommentlist || []).map(toFollowComment);
    } catch (error) {
      logApiError('followAPI.getComments', error, FOLLOW_GET_COMMENTS_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローコメントを保存する（followcommentno 省略時は新規登録、指定時は編集）
   * @param payload followno・commentcontent（編集時は followcommentno も）・noticeDestinationList（任意）
   * @returns 保存後の最新コメント一覧
   */
  saveFollowComment: async (payload: {
    followno: number;
    followcommentno?: number;
    commentcontent: string;
    noticeDestinationList?: { userno: number; urgentflag: boolean }[];
  }): Promise<FollowComment[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SAVE_COMMENT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローコメントの保存に失敗しました');
      }
      return (data.followcommentlist || []).map(toFollowComment);
    } catch (error) {
      logApiError('followAPI.saveFollowComment', error, FOLLOW_SAVE_COMMENT_API, 'POST');
      throw error;
    }
  },

  /**
   * フォローコメントを削除する
   * @param followcommentno 削除対象のコメントNo
   * @param followno 指定時は削除後の最新コメント一覧を返す
   */
  deleteComment: async (followcommentno: number, followno?: number): Promise<FollowComment[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_DELETE_COMMENT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followcommentno, followno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローコメントの削除に失敗しました');
      }
      return (data.followcommentlist || []).map(toFollowComment);
    } catch (error) {
      logApiError('followAPI.deleteComment', error, FOLLOW_DELETE_COMMENT_API, 'POST');
      throw error;
    }
  },

  /**
   * 添付ファイル一覧を取得する
   * @param params type: タイプ（1:フォロー、2:案件、3:フォーム回答No）, targetno: 主キーNo, targetoption: オプション
   * @returns 添付ファイル一覧
   */
  getAttachmentList: async (params: { type: number; targetno: number; targetoption?: string }): Promise<any> => {
    try {
      const requestBody = {
        type: params.type,
        targetno: params.targetno,
        targetoption: params.targetoption || ""
      };

      const response = await fetchWithTokenRefresh(ATTACHMENT_LIST_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '添付ファイル一覧の取得に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('添付ファイル一覧取得エラー:', error);
      logApiError('followAPI.getAttachmentList', error, ATTACHMENT_LIST_API, 'POST');
      throw error;
    }
  },
  
  /**
   * 添付ファイルをダウンロード／プレビュー用に取得する
   * @param params attachfileno: 添付ファイルNo, download: trueでダウンロード・falseでプレビュー, filename: ファイル名, companyid: 企業ID
   * @returns ファイルの Blob
   */
  downloadAttachment: async (params: { attachfileno: number; download: boolean; filename: string }): Promise<Blob> => {
    const response = await fetchWithTokenRefresh(ATTACHMENT_DOWNLOAD_API, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        attachfileno: params.attachfileno,
        download: params.download,
        filename: params.filename,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
    }

    return response.blob();
  },

  /**
   * 添付ファイルを削除する
   * @param params attachfileno: 添付ファイルNo, companyid: 企業ID
   * @returns APIレスポンス
   */
  deleteAttachment: async (params: { attachfileno: number; companyid: string | number }): Promise<any> => {
    try {
      const requestBody = {
        attachfileno: params.attachfileno,
        companyid: params.companyid
      };

      const response = await fetchWithTokenRefresh(ATTACHMENT_DELETE_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify(requestBody),
      }, /* skipRetry: 二重削除防止のため自動再試行しない */ true);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '添付ファイルの削除に失敗しました');
      }

      return data;
    } catch (error) {
      console.error('添付ファイル削除エラー:', error);
      logApiError('followAPI.deleteAttachment', error, ATTACHMENT_DELETE_API, 'POST');
      throw error;
    }
  },

  /**
   * 通知先ユーザー一覧を取得する
   * @param customerno 顧客番号
   * @param plantodoflag 予定/ToDoフラグ
   * @returns 通知先ユーザー一覧
   */
  getNoticeDestination: async (
    customerno: number,
    plantodoflag?: boolean
  ): Promise<NoticeDestinationUser[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_GET_NOTICE_DESTINATION_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ customerno, plantodoflag: plantodoflag ?? false }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || '通知先取得に失敗しました');
      }

      return data.noticedestinationlist || [];
    } catch (error) {
      logApiError('followAPI.getNoticeDestination', error, FOLLOW_GET_NOTICE_DESTINATION_API, 'POST');
      return [];
    }
  },

  /**
   * ホーム画面の ToDoカウントを取得する
   * @returns 未読フォロー件数（取得失敗時は 0）
   */
  getTodoCount: async (): Promise<{ followUnreadCount: number; followTempCount: number; followTodoCount: number }> => {
    try {
      const response = await fetchWithTokenRefresh(HOME_TODO_COUNT_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({}),
      });
      if (!response.ok) return { followUnreadCount: 0, followTempCount: 0, followTodoCount: 0 };
      const data = await response.json();
      return data.statuscode === 200
        ? {
            followUnreadCount: data.followUnreadCount ?? 0,
            followTempCount: data.followTempCount ?? 0,
            followTodoCount: data.followTodoCount ?? 0,
          }
        : { followUnreadCount: 0, followTempCount: 0, followTodoCount: 0 };
    } catch (error) {
      logApiError('followAPI.getTodoCount', error, HOME_TODO_COUNT_API, 'POST');
      return { followUnreadCount: 0, followTempCount: 0, followTodoCount: 0 };
    }
  },

  /**
   * 予定ToDoフォロー一覧を取得する
   * @returns 予定ToDoフォロー一覧
   */
  getTodoFollows: async (contactusernoList: number[]): Promise<any[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SEARCH_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ plantodoflag: true, todoType: 'today', contactusernoList, orderBy: 'flw.updatedate desc' }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data.statuscode === 200 ? (data.followlist || []).map(normalizeFollowdate) : [];
    } catch (error) {
      logApiError('followAPI.getTodoFollows', error, FOLLOW_SEARCH_API, 'POST');
      return [];
    }
  },

  /**
   * 仮登録フォロー一覧を取得する
   * @returns 仮登録フォロー一覧
   */
  getTemporaryFollows: async (contactusernoList: number[]): Promise<any[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SEARCH_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ temporaryOnly: true, contactusernoList, orderBy: 'flw.updatedate desc' }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data.statuscode === 200 ? (data.followlist || []).map(normalizeFollowdate) : [];
    } catch (error) {
      logApiError('followAPI.getTemporaryFollows', error, FOLLOW_SEARCH_API, 'POST');
      return [];
    }
  },

  /**
   * ログインユーザー宛の未確認フォロー一覧を取得する
   * @returns 未確認フォロー一覧（urgentflag 含む）
   */
  getUnconfirmedFollows: async (): Promise<any[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_SEARCH_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ unreadOnly: true, orderBy: 'flw.updatedate desc' }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      return data.statuscode === 200 ? (data.followlist || []).map(normalizeFollowdate) : [];
    } catch (error) {
      logApiError('followAPI.getUnconfirmedFollows', error, FOLLOW_SEARCH_API, 'POST');
      return [];
    }
  },

  /**
   * フォローの通知履歴を取得する
   * @param followno 対象フォローNo
   * @returns 通知履歴一覧
   */
  getNoticeHistory: async (followno: number): Promise<any[]> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_NOTICE_HISTORY_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followno }),
      });
      if (!response.ok) return [];
      const data = await response.json();
      if (data.statuscode !== 200) return [];
      // datalist をフラット化して「ユーザー宛て通知のみ」1行1通知先に変換
      return (data.datalist || []).flatMap((unit: any) =>
        (unit.noticeList || [])
          .filter((item: any) => item.notificationno !== null)
          .map((item: any) => ({
            noticedate: unit.noticedate,
            deptname: item.notificationDeptname,
            username: item.notificationUsername,
            urgentflag: item.urgentflag,
          }))
      );
    } catch (error) {
      logApiError('followAPI.getNoticeHistory', error, FOLLOW_NOTICE_HISTORY_API, 'POST');
      return [];
    }
  },

  /**
   * フォローを未読（未確認）に戻す
   * @param followno 対象フォローNo
   */
  markFollowUnread: async (followno: number): Promise<void> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_UNREAD_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ followno }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }
      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォローの未読化に失敗しました');
      }
    } catch (error) {
      logApiError('followAPI.markFollowUnread', error, FOLLOW_UNREAD_API, 'POST');
      throw error;
    }
  },

  /**
   * フォロー通知を送信する
   * @param followno 通知対象のフォローNo
   * @param noticeDestinationList 通知先一覧
   *   - ユーザー通知: { userno, urgentflag }
   *   - 外部メール通知: { mailaddress, urgentflag: false }（userno は省略）
   *   - Teams通知: { teamschannelurl }
   * @param alertParams 予定/ToDoのアラート設定（省略時は API ボディに含めない）
   * @returns Teams通知失敗時の警告メッセージ（DB登録は完了）、正常時は null
   */
  sendNotice: async (
    followno: number,
    noticeDestinationList: (
      | { userno: number; urgentflag: boolean }
      | { mailaddress: string; urgentflag: false }
      | { teamschannelurl: string }
    )[],
    alertParams?: {
      plantodoflag?: boolean;
      prealert?: string;
      realert?: string;
      realertflag?: boolean;
    }
  ): Promise<string | null> => {
    try {
      const response = await fetchWithTokenRefresh(FOLLOW_NOTICE_API, {
        method: 'POST',
        headers: JSON_HEADERS,
        // data ラッパーなし: FollowServiceFollowDto を直接送信
        body: JSON.stringify({
          followno,
          noticeDestinationList,
          ...(alertParams ?? {}),
        }),
      }, /* skipRetry: 通知の二重送信防止のため自動再試行しない */ true);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`APIエラー: ${response.status} ${response.statusText} ${errorText}`);
      }

      const data = await response.json();
      if (data.statuscode !== 200) {
        throw new Error(data.message || 'フォロー通知の送信に失敗しました');
      }

      // statuscode=200 かつ message あり → Teams通知失敗（DB登録は完了）
      return data.message ?? null;
    } catch (error) {
      logApiError('followAPI.sendNotice', error, FOLLOW_NOTICE_API, 'POST');
      throw error;
    }
  },
};